// Shared motion primitives for cinematic UI across Ameenji CRM.
// Keep durations short (200–400ms) and easings Apple-ish so the app
// feels premium without being tiring over an 8-hour shift.

export const easeApple = [0.16, 1, 0.3, 1];
export const easeOut = [0, 0, 0.2, 1];

export const pageTransition = {
  initial: { opacity: 0, y: 8, filter: "blur(2px)" },
  animate: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.4, ease: easeApple },
  },
  exit: {
    opacity: 0,
    y: -4,
    transition: { duration: 0.2, ease: easeApple },
  },
};

export const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.3, ease: easeApple } },
};

export const staggerContainer = {
  animate: {
    transition: { staggerChildren: 0.05, delayChildren: 0.05 },
  },
};

export const staggerContainerFast = {
  animate: {
    transition: { staggerChildren: 0.03 },
  },
};

export const itemReveal = {
  initial: { opacity: 0, y: 10 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: easeApple },
  },
};

export const itemFromLeft = {
  initial: { opacity: 0, x: -12 },
  animate: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.35, ease: easeApple },
  },
};

export const cardLift = {
  whileHover: {
    y: -2,
    transition: { duration: 0.2, ease: easeApple },
  },
  whileTap: { y: 0, scale: 0.99 },
};

// Hook: animate a number from 0 (or `from`) to `value` with apple-easing.
// Cheap CSS-only-ish: uses requestAnimationFrame, no framer-motion dep.
import React, { useEffect, useState, useRef } from "react";

export function useCountUp(value, { duration = 800, from = 0 } = {}) {
  const [display, setDisplay] = useState(from);
  const startRef = useRef(null);
  const fromRef = useRef(from);

  useEffect(() => {
    if (typeof value !== "number" || Number.isNaN(value)) {
      setDisplay(value);
      return undefined;
    }
    // Skip animation for "already-rendered same value" transitions.
    fromRef.current = display;
    startRef.current = null;

    let raf;
    const step = (ts) => {
      if (startRef.current === null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const progress = Math.min(1, elapsed / duration);
      // easeOutQuart for snappy-but-soft landing
      const eased = 1 - Math.pow(1 - progress, 4);
      const next = fromRef.current + (value - fromRef.current) * eased;
      setDisplay(next);
      if (progress < 1) {
        raf = requestAnimationFrame(step);
      } else {
        setDisplay(value);
      }
    };
    raf = requestAnimationFrame(step);
    return () => raf && cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  return display;
}

// Animated number — wraps useCountUp into a JSX-friendly component.
// Used across all dashboards for KPI tiles.
export function CountValue({ value, prefix = "", suffix = "", duration = 850, className }) {
  const safe = typeof value === "number" && !Number.isNaN(value) ? value : 0;
  const n = useCountUp(safe, { duration });
  const rounded = Math.round(n);
  return React.createElement(
    "span",
    { className: className ? `tabular-nums ${className}` : "tabular-nums" },
    `${prefix}${rounded.toLocaleString("en-IN")}${suffix}`
  );
}

